import { TabContext, TabList, TabPanel } from "@mui/lab";
import {Badge, Box, Grid, SxProps, Tab, TabProps, Theme, useTheme} from "@mui/material";
import {useEffect, useState} from "react";
import { useLocation } from "react-router-dom";
import DescriptionIcon from '@mui/icons-material/Description';
import TaskIcon from '@mui/icons-material/Task';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SummaryPanel from "./SummaryPanel";
import SolicitationDocumentation from "./SolicitationDocumentation";
import SolicitationTasks from "./tasks/SolicitationTasks";
import {
    Solicitation,
    SolicitationStatusViewDTO, SolicitationStatusViewDTOFields
} from "../../../types/solicitation/solicitationData";
import {SolicitationFinancePreferences} from "../../../types/financing/financingData";
import {HttpSolicitationFinancing} from "../../../http/financing/httpFinancing";
import {EntityWithIdFields} from "../../../types/baseEntities";
import SolicitationSummaryTab from "../tabs/SolicitationSummaryTab";
import {LoaderBlockUI} from "../../../components/loader";
import SolicitationGuarantee from "./SolicitationGuarantee";
import {HttpSolicitationOnboarding} from "../../../http/solicitation/httpSolicitationOnboarding";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TabListStyles: SxProps<Theme> = (theme: Theme) => (
    {
        '& .MuiTabs-indicator': {
            display: 'none'
        },
        '& .Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: `${theme.palette.common.white} !important`
        },
        '& .MuiTab-root': {
            minWidth: 65,
            minHeight: 40,
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            borderRadius: '5px',
            [theme.breakpoints.up('md')]: {
                minWidth: 130
            }
        }
    }

    // {
    //     '& .MuiTabs-indicator': {
    //         display: 'none'
    //     },

    //     '& .Mui-selected': {
    //         backgroundColor: theme.palette.primary.main,
    //         color: `${theme.palette.common.white} !important`
    //     },

    //     '& .MuiTab-root': {
    //         minWidth: 65,
    //         minHeight: 40,
    //         padding: '7.5px',

    //         borderRadius: '5px',
    //         [theme.breakpoints.up('md')]: {
    //             minWidth: 130
    //         }
    //     }
    // }
)


export enum EnumTabsSolicitud {
    Estado = 'estado',

    Resumen = 'resumen',
    Documentacion = 'documentacion',
    Tareas = 'tareas',
    Garantias = 'garantias'
}

interface SolicitationTabsProps {
    solicitud: Solicitation,
    reload?: () => void
}


interface CustomTabProps extends Omit<TabProps, 'label'> {
    label: string
    value: EnumTabsSolicitud
}

const CustomTab = (props: CustomTabProps) => {

    const { label, value, ...tabProps } = props;

    return (
        <Tab
            label={
                <Box sx={{ display: 'flex', alignItems: 'center', '& svg': { mr: 2 } }}>
                    {label}
                </Box>
            }
            value={value}
            {...tabProps}
        />
    )
}


const SolicitationTabs = (props: SolicitationTabsProps) => {

    const location = useLocation();
    const state = location.state;

    const [value, setValue] = useState<string>(state?.tab || EnumTabsSolicitud.Resumen);
    const [financingPreferences, setFinancingPreferences] = useState<SolicitationFinancePreferences>()
    const [solicitationStatus, setSolicitationStatus] = useState<SolicitationStatusViewDTO>()
    
    const onReload = () => { 
        props.reload && props.reload()
        loadGeneralData()
    }
    
    const loadGeneralData = () => {
        HttpSolicitationFinancing.get(props.solicitud[EntityWithIdFields.Id]).then((r) => {
            setFinancingPreferences(r)
        })
        HttpSolicitationOnboarding.getStatusById(props.solicitud[EntityWithIdFields.Id]).then((r) => setSolicitationStatus(r))
    }
    
    useEffect(() => {
        loadGeneralData()
    }, [props.solicitud])

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    const theme = useTheme();

    return (

        <Grid container spacing={6}>
            <Grid item xs={12}>
                {
                    financingPreferences ? 
                        <SummaryPanel solicitud={props.solicitud} preferencias={financingPreferences}/>
                        :
                        <LoaderBlockUI/>
                }
            </Grid>
            <Grid item xs={12}>
                <TabContext value={value}>
                    <TabList
                        variant='scrollable'
                        scrollButtons='auto'
                        onChange={handleChange}
                        sx={TabListStyles(theme)}
                    >
                        <CustomTab label={'Resumen'}
                                   value={EnumTabsSolicitud.Resumen}
                                   icon={<QueryStatsIcon/>}
                        />
                        <CustomTab label={'Documentación'}
                                   value={EnumTabsSolicitud.Documentacion}
                                   icon={
                            <Badge badgeContent={solicitationStatus ? solicitationStatus[SolicitationStatusViewDTOFields.PendantDocumentsQuantity] : 0}
                                   color='primary' sx={{marginTop: 2}}
                            >
                                <DescriptionIcon />
                            </Badge>
                        }
                        />
                        <CustomTab label='Tareas'
                                   value={EnumTabsSolicitud.Tareas}
                                   icon={
                                <Badge badgeContent={solicitationStatus ? solicitationStatus[SolicitationStatusViewDTOFields.PendantTasksQuantity] : 0}
                                       color='primary' sx={{marginTop: 2}}
                                >
                                    <TaskIcon />
                                </Badge>
                            }
                        />
                        <CustomTab label={'Garantías'}
                                   value={EnumTabsSolicitud.Garantias}
                                   icon={<AccountBalanceIcon/>}
                        />
                    </TabList>
                    <Box>
                        {
                            financingPreferences &&
                            <TabPanel value={EnumTabsSolicitud.Resumen}>
                                <SolicitationSummaryTab solicitation={props.solicitud} preferences={financingPreferences}/>
                            </TabPanel>
                        }
                        <TabPanel value={EnumTabsSolicitud.Documentacion}>
                            <SolicitationDocumentation solicitationId={props.solicitud[EntityWithIdFields.Id]} />
                        </TabPanel>
                        <TabPanel value={EnumTabsSolicitud.Tareas}>
                            <SolicitationTasks solicitationId={props.solicitud[EntityWithIdFields.Id]} />
                        </TabPanel>
                        <TabPanel value={EnumTabsSolicitud.Garantias}>
                            {
                                solicitationStatus &&
                                <SolicitationGuarantee solicitationId={props.solicitud[EntityWithIdFields.Id]}
                                                       solicitationStatus={solicitationStatus}
                                                       onReload={onReload}
                                />
                            }
                        </TabPanel>
                    </Box>
                </TabContext>
            </Grid>
        </Grid >
    )
}

export default SolicitationTabs;